import { createElement } from "react";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpwardRounded";
import SortIcon from "@mui/icons-material/ImportExportRounded";
import { CSSObject, Theme } from "@mui/material";
import { GridColDef, GridLocaleText, GridRowSelectionModel, GridSlotsComponent } from "@mui/x-data-grid";
import { deDE } from "@mui/x-data-grid/locales";

import { TypedGridColDef } from "./DataTable.types";

/**
 * Rows per page for every list view. 100 is the hard ceiling of the free (Community) DataGrid — it
 * throws on a larger `pageSize` instead of warning — so this cannot be raised without a Pro licence.
 */
export const DATA_TABLE_PAGE_SIZE = 100;

export function createColumns<T>(columns: Array<TypedGridColDef<T>>): GridColDef[] {
  return columns;
}

/**
 * Empty starting point for a grid's `rowSelectionModel`. A factory rather than a shared constant, so
 * every table gets its own `Set` instead of all of them selecting into one.
 */
export const emptyRowSelection = (): GridRowSelectionModel => ({ type: "include", ids: new Set<string>() });

// Keyed by i18next language code. Only German is bundled today (the app is German-only); any other
// language falls through to the DataGrid's built-in English locale.
export const dataTableLocales: Record<string, Partial<GridLocaleText>> = {
  de: deDE.components.MuiDataGrid.defaultProps.localeText,
};

/**
 * Shared sort-icon slots used by every grid wrapper (always-visible unsorted icon plus the
 * ascending/descending arrows). Kept here so {@link DataTable} and {@link GroupedDataTable}
 * can't drift apart.
 */
export const dataTableSortIconSlots: Partial<GridSlotsComponent> = {
  columnUnsortedIcon: () => createElement(SortIcon),
  columnSortedAscendingIcon: () => createElement(ArrowUpwardIcon),
  columnSortedDescendingIcon: () => createElement(ArrowDownwardIcon),
};

/**
 * Shared DataGrid "chrome": borderless grid, bold always-visible sort headers, hidden column
 * separators and odd-row striping. Both {@link DataTable} and {@link GroupedDataTable} build their
 * `sx` on top of this so the look stays in sync; component-specific rules (checkbox column, group
 * separator rows) are spread in afterwards by each wrapper.
 *
 * @param options.clickableRows adds a pointer cursor on row hover (rows that navigate on click).
 */
export function getDataTableChromeSx(theme: Theme, options?: { clickableRows?: boolean }): CSSObject {
  return {
    border: "none",
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
      fontSize: "0.875rem",
      whiteSpace: "normal",
    },
    "& .MuiDataGrid-iconButtonContainer": {
      visibility: "visible",
      width: "auto",
    },
    "& .MuiDataGrid-columnHeader .MuiDataGrid-sortButton": {
      opacity: 1,
    },
    "& .MuiDataGrid-columnHeader:not(:hover) .MuiDataGrid-sortButton": {
      visibility: "visible",
      opacity: 1,
    },
    // Sort and menu icons only. The selection column's header carries a checkbox rather than an
    // icon button, and resizing or recolouring it would leave the select-all box smaller and paler
    // than the row checkboxes it belongs with.
    "& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox) .MuiSvgIcon-root": {
      fontSize: "18px",
      color: `${theme.palette.action.active} !important`,
    },
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    "& .odd-row": {
      backgroundColor: theme.palette.fsw["grey-25"],
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "action.hover",
      ...(options?.clickableRows && { cursor: "pointer" }),
    },
  };
}
